/**
 * Teen Theme Context
 * Central state management for the personalized teen experience
 * Manages: age group, gender identity, theme preference, and visual adaptations
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme configurations for each age group
export const TEEN_THEMES = {
  // Younger Teens (12-14): "The Calm Cloud"
  younger: {
    id: 'younger',
    name: 'The Calm Cloud',
    ageRange: '12-14',
    mode: 'light',
    colors: {
      base: '#F8F9FA',
      baseDark: '#F1F5F9',
      surface: '#FFFFFF',
      surfaceHover: '#F8FAFC',
      primary: '#818CF8',
      primaryHover: '#6366F1',
      accent: '#F472B6',
      accentHover: '#EC4899',
      textPrimary: '#1E293B',
      textSecondary: '#64748B',
      textMuted: '#94A3B8',
      border: 'rgba(255, 255, 255, 0.5)',
      glow: 'rgba(129, 140, 248, 0.3)',
    },
    visual: {
      borderRadius: 'rounded-2xl',
      cardRadius: 'rounded-3xl',
      buttonRadius: 'rounded-full',
      shadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
      shadowHover: '0 12px 40px rgba(0, 0, 0, 0.08)',
      blur: 'backdrop-blur-xl',
      border: 'border-white/50',
    },
    vibe: 'Pastel & Soft. Optimistic, light, tactile with soft rounded elements.',
  },
  
  // Mid Teens (15-16): "The Digital Horizon"
  mid: {
    id: 'mid',
    name: 'The Digital Horizon',
    ageRange: '15-16',
    mode: 'dark',
    colors: {
      base: '#0F172A',
      baseDark: '#1E293B',
      surface: 'rgba(30, 41, 59, 0.7)',
      surfaceHover: 'rgba(30, 41, 59, 0.9)',
      primary: '#6366F1',
      primaryHover: '#818CF8',
      accent: '#38BDF8',
      accentHover: '#7DD3FC',
      textPrimary: '#F8FAFC',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      border: 'rgba(255, 255, 255, 0.1)',
      glow: 'rgba(99, 102, 241, 0.4)',
    },
    visual: {
      borderRadius: 'rounded-xl',
      cardRadius: 'rounded-2xl',
      buttonRadius: 'rounded-xl',
      shadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      shadowHover: '0 8px 30px rgba(0, 0, 0, 0.4)',
      blur: 'backdrop-blur-2xl',
      border: 'border-white/10',
    },
    vibe: 'Transitional, using glassmorphism and balanced contrast. More autonomy.',
  },
  
  // Older Teens (17-18): "The Midnight Zen"
  older: {
    id: 'older',
    name: 'The Midnight Zen',
    ageRange: '17-18',
    mode: 'dark',
    colors: {
      base: '#0A0A0A',
      baseDark: '#121212',
      surface: 'rgba(18, 18, 18, 0.6)',
      surfaceHover: 'rgba(28, 28, 28, 0.8)',
      primary: '#E0E7FF',
      primaryHover: '#C7D2FE',
      accent: '#818CF8',
      accentHover: '#A5B4FC',
      textPrimary: '#FFFFFF',
      textSecondary: '#A1A1AA',
      textMuted: '#71717A',
      border: 'rgba(255, 255, 255, 0.08)',
      glow: 'rgba(224, 231, 255, 0.15)',
    },
    visual: {
      borderRadius: 'rounded-lg',
      cardRadius: 'rounded-xl',
      buttonRadius: 'rounded-lg',
      shadow: 'none',
      shadowHover: '0 0 30px rgba(224, 231, 255, 0.1)',
      blur: 'backdrop-blur-2xl backdrop-saturate-150',
      border: 'border-white/[0.08]',
    },
    vibe: 'Premium, sophisticated, dark mode focused. Subtle depth, 1px borders, glow effects.',
  },
};

// Custom color themes users can choose within their age bracket
export const CUSTOM_PALETTES = {
  younger: [
    { id: 'cloud', name: 'Cloud', primary: '#818CF8', accent: '#F472B6', bg: '#F8F9FA' },
    { id: 'ocean', name: 'Ocean', primary: '#38BDF8', accent: '#34D399', bg: '#F0F9FF' },
    { id: 'sunset', name: 'Sunset', primary: '#FB923C', accent: '#F472B6', bg: '#FFFBEB' },
    { id: 'meadow', name: 'Meadow', primary: '#4ADE80', accent: '#A78BFA', bg: '#F0FDF4' },
  ],
  mid: [
    { id: 'horizon', name: 'Horizon', primary: '#6366F1', accent: '#38BDF8', bg: '#0F172A' },
    { id: 'neon', name: 'Neon', primary: '#22D3EE', accent: '#F472B6', bg: '#0C1222' },
    { id: 'aurora', name: 'Aurora', primary: '#A78BFA', accent: '#34D399', bg: '#0F172A' },
    { id: 'flame', name: 'Flame', primary: '#F97316', accent: '#FBBF24', bg: '#1C1917' },
  ],
  older: [
    { id: 'midnight', name: 'Midnight', primary: '#E0E7FF', accent: '#818CF8', bg: '#0A0A0A' },
    { id: 'obsidian', name: 'Obsidian', primary: '#F9FAFB', accent: '#6366F1', bg: '#09090B' },
    { id: 'ember', name: 'Ember', primary: '#FED7AA', accent: '#EA580C', bg: '#0C0A09' },
    { id: 'sage', name: 'Sage', primary: '#D1FAE5', accent: '#10B981', bg: '#0A0A0A' },
  ],
};

// Gender options (inclusive)
export const GENDER_OPTIONS = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
  { id: 'nonbinary', label: 'Non-binary' },
  { id: 'skip', label: 'Prefer not to say' },
];

// Age bracket options
export const AGE_OPTIONS = [
  { id: 'younger', label: '12-14', description: 'Early teens' },
  { id: 'mid', label: '15-16', description: 'Mid teens' },
  { id: 'older', label: '17-18', description: 'Older teens' },
];

// Context
const TeenThemeContext = createContext(null);

// Provider
export const TeenThemeProvider = ({ children }) => {
  const [ageGroup, setAgeGroup] = useState(null); // 'younger' | 'mid' | 'older'
  const [gender, setGender] = useState(null);
  const [customPalette, setCustomPalette] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);

  // Load saved preferences
  useEffect(() => {
    const saved = localStorage.getItem('reclaim_teen_preferences');
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        setAgeGroup(prefs.ageGroup);
        setGender(prefs.gender);
        setCustomPalette(prefs.customPalette);
        setIsOnboarded(prefs.isOnboarded);
        
        // Sync with simple flags for GlobalTeenTheme
        if (prefs.isOnboarded) {
          localStorage.setItem('teen_onboarded', 'true');
          localStorage.setItem('teen_age_bracket', prefs.ageGroup);
        }
      } catch (e) {
        console.log('Could not load teen preferences');
      }
    }
  }, []);

  // Save preferences
  const savePreferences = useCallback((prefs) => {
    const toSave = {
      ageGroup: prefs.ageGroup ?? ageGroup,
      gender: prefs.gender ?? gender,
      customPalette: prefs.customPalette ?? customPalette,
      isOnboarded: prefs.isOnboarded ?? isOnboarded,
    };
    localStorage.setItem('reclaim_teen_preferences', JSON.stringify(toSave));
  }, [ageGroup, gender, customPalette, isOnboarded]);

  // Complete onboarding
  const completeOnboarding = useCallback((data) => {
    setAgeGroup(data.ageGroup);
    setGender(data.gender);
    setIsOnboarded(true);
    savePreferences({ ...data, isOnboarded: true });
    
    // Also set simple flags for GlobalTeenTheme
    localStorage.setItem('teen_onboarded', 'true');
    localStorage.setItem('teen_age_bracket', data.ageGroup);
    if (data.customPalette) {
      localStorage.setItem('teen_selected_theme', JSON.stringify({
        ...data.customPalette,
        mode: data.ageGroup === '12-14' ? 'light' : 'dark',
      }));
    }
  }, [savePreferences]);

  // Update theme/palette
  const updatePalette = useCallback((palette) => {
    setCustomPalette(palette);
    savePreferences({ customPalette: palette });
  }, [savePreferences]);

  // Get current theme config
  const theme = ageGroup ? TEEN_THEMES[ageGroup] : TEEN_THEMES.mid;
  
  // Get available palettes for current age group
  const availablePalettes = ageGroup ? CUSTOM_PALETTES[ageGroup] : CUSTOM_PALETTES.mid;
  
  // Get current palette (custom or default)
  const currentPalette = customPalette || (availablePalettes ? availablePalettes[0] : null);

  // Generate CSS variables for current theme
  const cssVariables = {
    '--teen-base': theme.colors.base,
    '--teen-base-dark': theme.colors.baseDark,
    '--teen-surface': theme.colors.surface,
    '--teen-surface-hover': theme.colors.surfaceHover,
    '--teen-primary': currentPalette?.primary || theme.colors.primary,
    '--teen-primary-hover': theme.colors.primaryHover,
    '--teen-accent': currentPalette?.accent || theme.colors.accent,
    '--teen-accent-hover': theme.colors.accentHover,
    '--teen-text': theme.colors.textPrimary,
    '--teen-text-secondary': theme.colors.textSecondary,
    '--teen-text-muted': theme.colors.textMuted,
    '--teen-border': theme.colors.border,
    '--teen-glow': theme.colors.glow,
  };

  const value = {
    // State
    ageGroup,
    gender,
    customPalette,
    isOnboarded,
    showThemeSelector,
    
    // Theme config
    theme,
    currentPalette,
    availablePalettes,
    cssVariables,
    
    // Actions
    setAgeGroup,
    setGender,
    setShowThemeSelector,
    completeOnboarding,
    updatePalette,
    
    // Helpers
    isYounger: ageGroup === 'younger',
    isMid: ageGroup === 'mid',
    isOlder: ageGroup === 'older',
    isDarkMode: theme.mode === 'dark',
  };

  return (
    <TeenThemeContext.Provider value={value}>
      {children}
    </TeenThemeContext.Provider>
  );
};

// Hook
export const useTeenTheme = () => {
  const context = useContext(TeenThemeContext);
  if (!context) {
    throw new Error('useTeenTheme must be used within a TeenThemeProvider');
  }
  return context;
};

export default TeenThemeContext;
